import React, { MutableRefObject, useEffect, useRef, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import {
  FilterListInterface,
  FilterListPopover,
  FilterListPopoverProps,
  FilterListProps,
  ZeroState,
} from '@modernloop/shared/components';
import { PublicError } from '@modernloop/shared/helper-components';
import { SearchIcon } from '@modernloop/shared/icons';
import {
  AutocompleteChangeReason,
  AutocompleteRenderGroupParams,
  Button,
  ListSubheader,
  PaperProps as MuiPaperProps,
  Stack,
  Typography,
} from '@mui/material';
import { has } from 'lodash';

import FilterButton from 'src/components/FilterButton';
import { BaseProps } from 'src/components/types';

export enum FilterSelectGroup {
  SELECTED = 'selected',
  SUGGESTED = 'suggested',
  OTHER = 'other',
}

export type FilterListSelectInterface = {
  dismiss: () => void;
};

export type Props<TData> = BaseProps & {
  options: TData[];
  values?: TData[];
  loading?: FilterListProps<TData>['loading'];
  error?: ApolloError;
  isOptionEqualToValue?: FilterListProps<TData>['isOptionEqualToValue'];
  placeholderText: FilterListProps<TData>['placeholderText'];
  filterListSelectInterfaceRef?: MutableRefObject<FilterListSelectInterface | null>;

  PaperProps?: MuiPaperProps;
  onPopoverOpen?: () => void;
  getLabel: () => string | React.ReactNode;
  getOptionLabel: FilterListProps<TData>['getOptionLabel'];
  renderOption?: FilterListProps<TData>['renderOption'];
  renderGroup?: FilterListProps<TData>['renderGroup'];
  groupBy?: FilterListProps<TData>['groupBy'];
  onChange: FilterListProps<TData>['onChange'];
  onPopoverClose?: FilterListPopoverProps<TData>['onPopoverClose'];
  onInputChange?: FilterListProps<TData>['onInputChange'];
  getChildSize?: (child: React.ReactNode) => number;
  filterOptions?: FilterListProps<TData>['filterOptions'];
  onScrollToEnd?: FilterListProps<TData>['onScrollToEnd'];
  filterSelectedOptions?: FilterListProps<TData>['filterSelectedOptions'];
  header?: React.ReactNode;
  hideClear?: boolean;
  startIcon?: JSX.Element;
  zeroState?: JSX.Element;
};

const DEFAULT_ITEM_HEIGHT = 32;
const NUMBER_OF_ITEMS = 10;
// This includes the height of search bar (40px) and 8px padding top and bottom
const SEARCH_BAR_HEIGHT_WITH_PADDING = 56;

const FilterSelect = <TData,>({
  options,
  values,
  loading,
  error,
  placeholderText,
  PaperProps,
  filterListSelectInterfaceRef,
  dataTestId,
  getLabel,
  getOptionLabel,
  isOptionEqualToValue,
  onChange,
  onPopoverClose,
  onInputChange,
  renderGroup,
  renderOption,
  groupBy,
  getChildSize,
  filterOptions,
  onScrollToEnd,
  filterSelectedOptions,
  header,
  hideClear,
  startIcon,
  zeroState,
  onPopoverOpen,
}: Props<TData>): JSX.Element => {
  const { t } = useTranslation();
  const [showFilterSelect, setShowFilterSelect] = useState(false);

  const jobButtonRef = useRef<HTMLButtonElement>(null);
  const filterListInterfaceRef = useRef<FilterListInterface | null>(null);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (!filterListSelectInterfaceRef) {
      return;
    }
    filterListSelectInterfaceRef.current = {
      dismiss: () => setShowFilterSelect(false),
    };
  }, [filterListSelectInterfaceRef]);

  const getListHeight = () => {
    if (options.length > NUMBER_OF_ITEMS) {
      return NUMBER_OF_ITEMS * DEFAULT_ITEM_HEIGHT;
    }
    return options.length * DEFAULT_ITEM_HEIGHT + SEARCH_BAR_HEIGHT_WITH_PADDING;
  };

  const handleClear = () => {
    if (!filterListInterfaceRef.current) {
      return;
    }
    filterListInterfaceRef.current.clearSelected();
  };

  const handleChange = (event: React.ChangeEvent, value: TData[], reason: AutocompleteChangeReason) => {
    if (onChange) {
      onChange(event, value, reason);
    }
  };

  const handleClose = (event, reason: 'backdropClick' | 'escapeKeyDown') => {
    setShowFilterSelect(false);

    if (onPopoverClose) {
      onPopoverClose(event, reason);
    }
  };

  const openFilterSelect = () => {
    if (!showFilterSelect && onPopoverOpen) {
      onPopoverOpen();
    }
    setShowFilterSelect(true);
  };

  const defaultRenderGroup = (params: AutocompleteRenderGroupParams) => [
    <ListSubheader key={params.key} component="div" disableGutters>
      <Stack alignItems="center" justifyContent="space-between" direction="row">
        <Typography
          fontWeight={600}
          variant="caption"
          sx={{
            display: 'flex',
            alignItems: 'center',
            paddingLeft: (theme) => theme.spacing(1),
            paddingTop: (theme) => theme.spacing(1.5),
          }}
        >
          {params.group === FilterSelectGroup.SUGGESTED && t('hiring-dashboard-filter:filter.groupSuggestions')}
          {params.group === FilterSelectGroup.SELECTED && t('hiring-dashboard-filter:filter.groupSelected')}
          {params.group === FilterSelectGroup.OTHER && t('hiring-dashboard-filter:filter.groupOther')}
        </Typography>
        {!hideClear && params.group === FilterSelectGroup.SELECTED && (
          <Button
            variant="text"
            color="primary"
            onClick={handleClear}
            sx={{ pr: 1.5, ':hover': { backgroundColor: 'transparent' } }}
          >
            <Typography variant="subtitle2">{t('hiring-dashboard-common:common.search.clearAll')}</Typography>
          </Button>
        )}
      </Stack>
    </ListSubheader>,
    params.children,
  ];

  const label = getLabel();

  const defaultIsOptionEqualToValue = (option: TData, value: TData) => {
    if (!option || !value) {
      return false;
    }
    if (
      (typeof option === 'string' && typeof value === 'string') ||
      (typeof option === 'number' && typeof value === 'number') ||
      (typeof option === 'boolean' && typeof value === 'boolean')
    ) {
      return option === value;
    }

    if (typeof option === 'object' && typeof value === 'object' && has(option, 'id') && has(value, 'id')) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      return option?.['id'] === value?.['id'];
    }

    return false;
  };

  return (
    <FilterListPopover
      dataTestId={dataTestId}
      filterSelectedOptions={filterSelectedOptions}
      autoFocus
      open={showFilterSelect}
      PaperProps={(PaperProps || { style: { width: 400 } }) as FilterListPopoverProps<TData>['PaperProps']}
      placeholderText={placeholderText}
      loading={loading}
      options={options}
      values={values}
      noOptionsText={
        zeroState || (
          <ZeroState
            label={t('hiring-dashboard-common:common.noResultsFound')}
            icon={<SearchIcon sx={{ width: '80px', height: '80px' }} />}
            actions={<Button onClick={handleClear}>{t('hiring-dashboard-filter:filter.clearSearch')}</Button>}
          />
        )
      }
      getOptionLabel={getOptionLabel}
      groupBy={groupBy}
      renderGroup={renderGroup || defaultRenderGroup}
      renderOption={renderOption}
      isOptionEqualToValue={isOptionEqualToValue || defaultIsOptionEqualToValue}
      filterListInterfaceRef={filterListInterfaceRef}
      listChildren={
        <>
          {header}
          <PublicError error={error} />
        </>
      }
      onChange={handleChange}
      onPopoverClose={handleClose}
      getChildSize={getChildSize || (() => DEFAULT_ITEM_HEIGHT)}
      getListHeight={getListHeight}
      onInputChange={onInputChange}
      filterOptions={filterOptions}
      onScrollToEnd={onScrollToEnd}
    >
      {React.isValidElement(label) &&
        React.cloneElement(label as JSX.Element, {
          onClick: openFilterSelect,
          ref: jobButtonRef,
        })}
      {!React.isValidElement(label) && (
        <FilterButton
          ref={jobButtonRef}
          hasSelections={Boolean(values?.length)}
          onClick={openFilterSelect}
          dataTestId="filter-select-button"
          startIcon={startIcon}
        >
          {label}
        </FilterButton>
      )}
    </FilterListPopover>
  );
};

export function defaultGetLabel<TData>(selectedValues: TData[], label, pluralLabel?: string) {
  if (selectedValues.length) {
    return selectedValues.length === 1
      ? `${selectedValues.length} ${label}`
      : `${selectedValues.length} ${pluralLabel || `${label}s`}`;
  }
  return label;
}

export default FilterSelect;
